import { useEffect } from "react";
import { getTreatmentLaunchContext } from "./launchContext";
import { PREVIEW_PROTOCOL } from "./previewProtocolConstants";
import "./questionnairePreviewFrame.css";

const resolveParentOrigin = () => {
  const params = new URLSearchParams(window.location.search);
  const requestedOrigin = params.get("parent_origin");
  if (!requestedOrigin || window.parent === window) return null;

  try {
    const normalized = new URL(requestedOrigin).origin;
    const referrerOrigin = document.referrer
      ? new URL(document.referrer).origin
      : null;
    return !referrerOrigin || referrerOrigin === normalized ? normalized : null;
  } catch {
    return null;
  }
};

const previewIsActive = () =>
  Boolean(
    getTreatmentLaunchContext(window.location.search).previewCapabilityToken,
  );

const isEmbeddedInIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export const isTrustedPreviewMessage = (event, parentOrigin) =>
  Boolean(
    parentOrigin &&
    event.origin === parentOrigin &&
    event.source === window.parent &&
    event.data?.version === PREVIEW_PROTOCOL.version,
  );

export const shouldReportPreviewError = (isReady) => !isReady;

const createEmitter = (parentOrigin) => (type, payload = {}) => {
  if (!parentOrigin || window.parent === window) return;
  window.parent.postMessage(
    { type, version: PREVIEW_PROTOCOL.version, ...payload },
    parentOrigin,
  );
};

const hasStablePreviewFrame = () => {
  const root = document.getElementById("root");
  if (!root) return false;
  const text = root.textContent?.trim().toLowerCase() || "";
  const hasPatientControl = Boolean(
    root.querySelector("button, input, [role='alert']"),
  );
  return hasPatientControl && !text.startsWith("loading");
};

export function QuestionnairePreviewBridge() {
  useEffect(() => {
    if (!previewIsActive()) return undefined;
    const applyPreviewFrame = isEmbeddedInIframe();
    if (applyPreviewFrame) {
      document.documentElement.classList.add("questionnaire-preview-mode");
    }
    const resetScroll = () => {
      window.scrollTo({ left: 0, top: 0 });
      document.scrollingElement?.scrollTo({ left: 0, top: 0 });
    };
    window.requestAnimationFrame(resetScroll);
    const scrollResetTimer = window.setTimeout(resetScroll, 0);
    const parentOrigin = resolveParentOrigin();
    const emit = createEmitter(parentOrigin);
    let previewIsReady = false;

    const emitNavigation = () => emit(PREVIEW_PROTOCOL.messages.navigation, {
      pathname: window.location.pathname,
      search: window.location.search,
    });
    const emitError = (message) => emit(PREVIEW_PROTOCOL.messages.error, {
      message: String(message || "The questionnaire preview encountered an error."),
    });
    const handleMessage = (event) => {
      if (!isTrustedPreviewMessage(event, parentOrigin)) return;
      if (event.data.type === PREVIEW_PROTOCOL.messages.refresh) {
        window.location.reload();
      } else if (
        event.data.type === PREVIEW_PROTOCOL.messages.identity &&
        Number.isInteger(event.data.requestId) &&
        Object.values(PREVIEW_PROTOCOL.identities).includes(event.data.identity)
      ) {
        const url = new URL(window.location.href);
        url.searchParams.set("preview_identity", event.data.identity);
        window.history.replaceState({}, "", url);
        window.dispatchEvent(
          new CustomEvent(PREVIEW_PROTOCOL.events.identityChanged, {
            detail: {
              requestId: event.data.requestId,
              receivedAt: performance.now(),
            },
          }),
        );
      }
    };
    const handleIdentityReady = (event) => {
      emit(PREVIEW_PROTOCOL.messages.identityReady, {
        identity: event.detail?.identity,
        requestId: event.detail?.requestId || null,
        message: event.detail?.message || "",
        resetDurationMs: event.detail?.resetDurationMs || null,
        requestReceivedAt: event.detail?.requestReceivedAt || null,
      });
    };
    const handleIdentityState = (event) => {
      if (event.detail?.source !== "local") return;
      emit(PREVIEW_PROTOCOL.messages.identityState, {
        identity: getTreatmentLaunchContext(window.location.search).previewCapabilityToken
          ? new URLSearchParams(window.location.search).get("preview_identity")
          : null,
      });
    };
    const emitReady = () => {
      previewIsReady = true;
      emit(PREVIEW_PROTOCOL.messages.ready);
    };
    // Once the questionnaire is visible, a late browser diagnostic must not
    // make the Admin Portal hide the iframe. The preview can make non-critical
    // background requests while a user is interacting with it.
    const handleError = (event) => {
      if (shouldReportPreviewError(previewIsReady)) {
        emitError(event.error?.message || event.message);
      }
    };
    const handleRejection = (event) => {
      if (shouldReportPreviewError(previewIsReady)) {
        emitError(event.reason?.message || event.reason);
      }
    };

    const observer = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(([entry]) => {
        emit(PREVIEW_PROTOCOL.messages.resize, {
          height: Math.ceil(entry.contentRect.height),
        });
      });
    const readinessObserver = new MutationObserver(() => {
      if (!hasStablePreviewFrame()) return;
      emitReady();
      readinessObserver.disconnect();
    });

    observer?.observe(document.documentElement);
    readinessObserver.observe(document.getElementById("root") || document.body, {
      childList: true,
      subtree: true,
    });
    window.addEventListener("message", handleMessage);
    window.addEventListener(
      PREVIEW_PROTOCOL.events.identityReady,
      handleIdentityReady,
    );
    window.addEventListener(
      PREVIEW_PROTOCOL.events.identityChanged,
      handleIdentityState,
    );
    window.addEventListener("popstate", emitNavigation);
    window.addEventListener("hashchange", emitNavigation);
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);
    if (hasStablePreviewFrame()) {
      emitReady();
      readinessObserver.disconnect();
    }
    emitNavigation();

    return () => {
      if (applyPreviewFrame) {
        document.documentElement.classList.remove("questionnaire-preview-mode");
      }
      window.clearTimeout(scrollResetTimer);
      observer?.disconnect();
      readinessObserver.disconnect();
      window.removeEventListener("message", handleMessage);
      window.removeEventListener(
        PREVIEW_PROTOCOL.events.identityReady,
        handleIdentityReady,
      );
      window.removeEventListener(
        PREVIEW_PROTOCOL.events.identityChanged,
        handleIdentityState,
      );
      window.removeEventListener("popstate", emitNavigation);
      window.removeEventListener("hashchange", emitNavigation);
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  return null;
}
